// src/pages/locations/countries/EditCountryPage.tsx
import React from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useCountry, useUpdateCountryMutation } from "@/api/features/locations";
import CountryForm from "@/components/locations/forms/CountryForm";

import LocationFormPage from "../components/LocationFormPage";
import { useLeaveConfirm } from "../components/form/useLeaveConfirm";
import { apiErrorSummary } from "../formErrors";
import { locationTabPath } from "../locations.access";
import { withFlash } from "../flash";

const EditCountryPage: React.FC = () => {
  const navigate = useNavigate();
  const { iso2: rawIso2 } = useParams<{ iso2: string }>();
  const iso2 = String(rawIso2 ?? "").trim().toUpperCase();

  const backTo = locationTabPath("countries");

  const { data: country, isLoading, error: loadError } = useCountry(iso2);
  const updateMutation = useUpdateCountryMutation();
  const { requestCancel, dialog } = useLeaveConfirm(() => navigate(backTo));

  const error = !iso2 ? "No country was specified." : apiErrorSummary(loadError);

  return (
    <LocationFormPage
      entity="countries"
      title="Edit country"
      subtitle={country ? `${country.name} (${iso2})` : undefined}
      backTo={backTo}
      backLabel="Back to Countries"
      isLoading={isLoading}
      loadingLabel="Loading country…"
      error={error}
    >
      {country && (
        <>
          <CountryForm
            mode="edit"
            value={country}
            isSubmitting={updateMutation.isPending}
            error={updateMutation.error}
            onCancel={requestCancel}
            onSubmit={(values) =>
              updateMutation.mutate(
                {
                  iso2,
                  body: {
                    name: values.name,
                    nameNorm: values.nameNorm || null,
                  },
                },
                {
                  onSuccess: (updated) =>
                    navigate(backTo, withFlash(`${updated.name} was updated.`)),
                }
              )
            }
          />

          {dialog}
        </>
      )}
    </LocationFormPage>
  );
};

export default EditCountryPage;
